import { ApolloError } from '@apollo/client';
import IconArrowRight from '@apollo/icons/default/IconArrowRight.svg';
import {
  Admonition,
  AdmonitionBody,
  AdmonitionHeader,
  AdmonitionTitle,
  Group,
} from '@apollo/orbit';
import { compare } from 'compare-versions';
import React from 'react';

import { ContactSupportLink } from 'src/components/ContactSupportCTAs';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { BuildPipelineTrackBadge } from 'src/lib/graphqlTypes/types';

import { FED_NEXT_VERSION } from '../../../../../lib/config/config';
import { BuildPipelineBadge } from '../BuildPipelineBadge';

import { ProposedChangesNotice } from './ProposedChangesNotice';

export const ProposedChangesSection = ({
  currentVersionTrack,
  proposedVersionTrack,
  error,
  isGraph,
}: {
  currentVersionTrack: NonNullable<
    GraphQLTypes.FederationVersionInfoFragment['allowedTracks'][0]
  >;
  proposedVersionTrack: NonNullable<
    GraphQLTypes.FederationVersionInfoFragment['allowedTracks'][0]
  >;
  error: ApolloError | undefined;
  isGraph: boolean | undefined;
}) => {
  const {
    badge: currentVersionBadge,
    compositionVersion: currentVersion,
    displayName: currentVersionName,
  } = currentVersionTrack;

  const {
    badge: proposedVersionBadge,
    compositionVersion: proposedVersion,
    displayName: proposedVersionName,
  } = proposedVersionTrack;

  const isDowngrading =
    // fed next should always be the latest available so all others should be below it
    (currentVersion === FED_NEXT_VERSION &&
      proposedVersion !== FED_NEXT_VERSION) ||
    compare(currentVersion, proposedVersion, '>');
  const upgradingToExperimental =
    proposedVersionBadge === BuildPipelineTrackBadge.EXPERIMENTAL;

  return (
    <Admonition
      variant={upgradingToExperimental || isDowngrading ? 'warning' : 'neutral'}
    >
      <AdmonitionHeader>
        <AdmonitionTitle>Proposed changes</AdmonitionTitle>
      </AdmonitionHeader>
      <AdmonitionBody>
        <Group orientation="vertical" gap={2}>
          <div className="flex items-center">
            <span>{currentVersionName}</span>
            {currentVersionBadge && (
              <BuildPipelineBadge
                status={currentVersionBadge}
                className="ml-1"
              />
            )}
            <IconArrowRight className="mx-2 text-placeholder" />
            <span>{proposedVersionName}</span>
            {proposedVersionBadge && (
              <BuildPipelineBadge
                status={proposedVersionBadge}
                className="ml-1"
              />
            )}
          </div>
          {upgradingToExperimental && !error && (
            <ProposedChangesNotice
              boldText="The selected Federation version is currently experimental."
              description="Until it reaches general availability, do not use this version in
          any production variant."
            />
          )}
          {isDowngrading && !error && (
            <ProposedChangesNotice
              boldText="Downgrading your Federation version can break your build!"
              description="Before you save, make sure none of your subgraphs targets a
                Federation version that's higher than this downgraded version."
            />
          )}
          {error && (
            <ErrorMessage className="mt-2">
              {`Unexpected error updating the federation version for this ${
                isGraph ? 'graph' : 'variant'
              }.`}{' '}
              Contact <ContactSupportLink>Apollo Support</ContactSupportLink> if
              this happens again.
            </ErrorMessage>
          )}
        </Group>
      </AdmonitionBody>
    </Admonition>
  );
};
